/**
 * @Module AuthNPolicyType
 */
import { get } from 'lodash';

import { AuthnPolicyConsts } from 'client/util/Constants';
import AuthNPolicyConditionsType, { formatAuthNPolicyConditionsForApi } from 'client/Security/AuthNPolicyConditionsType';

const { policyActions } = AuthnPolicyConsts;

export const EffectAction = new Map();
EffectAction.set(policyActions.allow, 'allow')
  .set(policyActions.allowWithMFA, 'allowwithmfa')
  .set(policyActions.deny, 'deny');

export const authnPolicyType = {
  userportal: 'user_portal',
  application: 'application',
  ldap: 'ldap',
};

export const authnPolicyTypeLabel = {
  [authnPolicyType.userportal]: 'User Portal',
  [authnPolicyType.application]: 'SSO Applications',
  [authnPolicyType.ldap]: 'JumpCloud LDAP',
};

export const authnPolicyActionLabel = {
  [policyActions.allow]: 'Allow',
  // TODO: Update if/when allowWithMFA can be saved to DB as unique action type
  // Until then sorting by action in the ItemTable will not work correctly
  [policyActions.allowWithMFA]: 'Allow',
  [policyActions.deny]: 'Deny',
};

const duo = 'duo';
const push = 'push';
const totp = 'totp';
const webauthn = 'webauthn';
export const authnPolicyAllowedMfaTypes = {
  [authnPolicyType.userportal]: [push, totp, webauthn, duo],
  [authnPolicyType.application]: [push, totp, webauthn, duo],
  [authnPolicyType.ldap]: [push, totp],
};

const defaultAuthNPolicy = () => ({
  description: '',
  disabled: true,
  effect: {
    action: policyActions.allow,
    obligations: {},
  },
  name: '',
  targets: {},
  type: authnPolicyType.userportal,
});

function getTargetResourceIds(targets) {
  return (targets.resources || []).filter(r => !!r.id).map(r => r.id);
}

function parseTargets(data) {
  if (data.type === authnPolicyType.application) {
    // Because this is a data model initialization method we don't need to enforce immutablity.
    // eslint-disable-next-line no-param-reassign
    data.targets.applications = getTargetResourceIds(data.targets);
  }

  // eslint-disable-next-line no-param-reassign
  data.targets.users = {
    inclusions: get(data.targets.users, 'inclusions', ['all']),
  };

  // eslint-disable-next-line no-param-reassign
  data.targets.userAttributes = {
    inclusions: get(data.targets.userAttributes, 'inclusions', []),
    exclusions: get(data.targets.userAttributes, 'exclusions', []),
  };

  return data.targets;
}

/**
 * @typedef AuthNPolicy
 * @property {string} type - the resource type protected by the policy.
 * @property {string} typeLabel - the policy type label used in display.
 * @property {integer} id - the integer id given to the policy
 * @property {string} [description] - user provided description
 * @property {boolean} disabled - whether the policy is actively protecting resources
 * @property {AuthNPolciyConditions} conditions - the conditions for enforcing the policy
 * @property {object} effect - object
 * @property {enum} effect.action - what the user will experience if the policy targets them
 * @property {object} effect.obligations - what the user must provide to authenticate if the policy
 * targets them. Usually a form of MFA.
 * @property {string} name - user provided name of the policy
 * @property {object} targets
 * @property {object} targets.[user]
 * @property {[]string} targets.[users].[inclusions] - an array with only the string "all"
 * or an array of ids of users
 * @property {[]string} targets.[users].[exclusions] - ids of users not targeted by the policy
 * @property {object} targets.[userGroups]
 * @property {[]string} targets.[userGroups].[inclusions] - the string "all" or ids of users groups
 * @property {[]string} targets.[userGroups].[exclusions] - ids of user groups not targeted
 * by the policy
 * @property {object} targets.[userAttributes]
 * @property {[]object} targets.[userAttributes].[inclusions] -
 * object of included user attrs { field: string, operator: string, value: boolean }
 * @property {[]object} targets.[userAttributes].[exclusions] -
 * object of excluded user attrs { field: string, operator: string, value: boolean }
 * @property {[]{type: string, [id]: string}} targets.[resources] - list of resources
 * protected by the policy. Only "type" available is "USER_PORTAL" for now, 9/17/20.
 * @property {string} action - modified string version of effect.action for displaying in UI
 */

/**
 * Constructor for individual items from /api/v2/authn/policies
 *
 * Apparently data has to be flat to pass through ItemTable columns!
 *
 * @export AuthNPolicy constructor
 * @param {object} [args={}] API response
 * @param {object} [defaultPolicy=defaultAuthNPolicy()]
 * @returns AuthNPolicy
 */
export default function AuthNPolicy(args = {}, defaultPolicy = defaultAuthNPolicy()) {
  const data = {
    ...defaultPolicy,
    ...args,
  };

  const getAction = (action) => {
    const actionType = EffectAction.get(action) || EffectAction.get(policyActions.allow);
    const isMFARequired = get(data, 'effect.obligations.mfa.required', false);

    return actionType === policyActions.allow && isMFARequired
      ? policyActions.allowWithMFA
      : actionType;
  };

  this.type = data.type;
  this.typeLabel = authnPolicyTypeLabel[data.type];
  if (data.id) {
    this.id = data.id;
  }
  this.description = data.description || '';
  // Is the policy itself disabled or not
  this.disabled = data.disabled === true;
  this.effect = {
    obligations: data.effect.obligations,
  };
  this.name = data.name || '';
  this.targets = parseTargets(data);

  this.conditions = new AuthNPolicyConditionsType(data.conditions);

  const action = getAction(data.effect && data.effect.action);

  this.action = action;
  this.actionLabel = authnPolicyActionLabel[action];

  return this;
}

function buildResourcesForApi(data) {
  let resources = [];
  if (data.type === authnPolicyType.application && data.targets.applications) {
    resources = data.targets.applications.map(id => ({
      type: authnPolicyType.application,
      id,
    }));
  }

  // This covers both user_portal and "all applications"
  if (resources.length === 0) {
    resources = [{ type: data.type }];
  }

  return resources;
}

function buildTargetsforApi(data) {
  const targets = get(data, 'targets', {});

  // Currently the API will only accept userAttributes for LDAP policies
  // Update accordingly when necessary
  if (data.type !== authnPolicyType.ldap) {
    delete targets.userAttributes;
  }

  return {
    ...targets,
    resources: buildResourcesForApi(data),
  };
}

// 'allowWithMFA' is only a valid action client side
// it must be converted to 'allow' with correct obligations
function formatItemActionForApi(action) {
  const actionType = EffectAction.get(action) || EffectAction.get(policyActions.allow);

  return actionType === policyActions.allowWithMFA
    ? policyActions.allow
    : actionType;
}

/**
 * Converts and Validates an AuthNPolicy to the model the API uses
 * @param {AuthNPolicy} item
 * @returns {object}
 */
export function formatAuthNPolicyForApi(item) {
  const apiItem = {
    id: item.id,
    name: item.name,
    assignments: item.assignments,
    conditions: formatAuthNPolicyConditionsForApi(item.conditions),
    description: item.description,
    disabled: item.disabled,
    effect: {
      ...item.effect,
      action: formatItemActionForApi(item.action),
    },
    targets: buildTargetsforApi(item),
    type: item.type,
  };

  // Delete the applications array since it is only for the UI,
  // and the API wants the data on targets.resources.
  delete apiItem.targets.applications;

  // Validation
  if (
    apiItem.targets.userGroups != null
    && Object.keys(apiItem.targets.userGroups).length === 0
  ) {
    delete apiItem.targets.userGroups;
  }

  if (
    apiItem.targets.users != null
    && ((
      apiItem.targets.users.inclusions != null
      && apiItem.targets.users.inclusions.length === 0
    ) || (
      Object.keys(apiItem.targets.users).length === 0
    ))
  ) {
    delete apiItem.targets.users;
  }

  if (apiItem.targets.userGroups == null && apiItem.targets.users == null) {
    apiItem.targets.users = { inclusions: ['all'] };
  }

  if (
    apiItem.effect && apiItem.effect.obligations
    && (
      Object.keys(apiItem.effect.obligations).length === 0
      || apiItem.effect.action === 'deny'
    )
  ) {
    delete apiItem.effect.obligations;
  }

  if (apiItem.conditions == null) {
    apiItem.conditions = {};
  }

  // Don't include id at all if it's null or 0
  if (apiItem.id == null || apiItem.id === 0) {
    delete apiItem.id;
  }

  return apiItem;
}
